/**
 * PendingChangesBar — the app's explicit-save affordance for edit-in-place forms.
 *
 * A floating dark pill, centred over the bottom of the viewport, that appears
 * only when there are unsaved edits. On a long form (several funding entities,
 * the servicing block, the wire ticket sitting under its release checks) a save
 * button parked below the last field is off-screen exactly when you need it, and
 * nothing tells you edits are pending while you scroll.
 *
 * Born on the correspondent detail tabs; shared since the Wire & Funding wire
 * ticket adopted it. It stays purely presentational — the caller owns `dirty` /
 * `saving` and does the persisting.
 *
 * Radius system: outer radius = inner radius + inner padding. The buttons are
 * 8px and the pill's padding is 8px, so the container is 16px — nested corners
 * share a centre and nothing fights the save button's curve.
 *
 * Motion: slides up with a slight spring on entry, slides back down on exit,
 * an amber dot pings while dirty, and `nudge()` shakes it horizontally when
 * someone tries to leave with unsaved work. All of it collapses to a plain fade
 * under `prefers-reduced-motion`.
 *
 * ⌘S / Ctrl+S saves while the pill is showing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { teal } from "../../theme/colors";

const PILL = {
  bg: "#22303c",
  text: "#eef2f5",
  muted: "#c6d0d9",
  amber: "#e8a13c",
  success: "#2e9e6b",
};

// Exit takes this long; the node stays mounted until it finishes.
const EXIT_MS = 320;
// How long the green "Saved" confirmation holds before sliding away.
const SAVED_MS = 1100;

export default function PendingChangesBar({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  label = "Save changes",
  count,
  subText,
  // "viewport" (default): fixed over the bottom of the window. "container":
  // absolute over the bottom of the nearest positioned ancestor — for a
  // sidebar panel, so the pill floats inside the panel rather than across
  // the page (and under the panel's own z-index).
  placement = "viewport",
}) {
  // `mounted` keeps the node alive through the exit transition; `shown` drives
  // the transform/opacity so the entry animation runs after the first paint.
  const [mounted, setMounted] = useState(dirty);
  const [shown, setShown] = useState(false);
  const [saved, setSaved] = useState(false);
  const [nudging, setNudging] = useState(false);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (dirty) {
      setSaved(false);
      setMounted(true);
      // One frame before flipping `shown`, or the browser has nothing to
      // transition from.
      const t = setTimeout(() => setShown(true), 20);
      return () => clearTimeout(t);
    }
    // Clean again. If that happened at the end of a save, hold the green
    // confirmation for a beat; a discard just leaves.
    if (wasSaving.current) {
      setSaved(true);
      const hold = setTimeout(() => setShown(false), SAVED_MS);
      const gone = setTimeout(() => setMounted(false), SAVED_MS + EXIT_MS);
      return () => {
        clearTimeout(hold);
        clearTimeout(gone);
      };
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [dirty]);

  useEffect(() => {
    if (saving) wasSaving.current = true;
    // A save that ended while the form is STILL dirty didn't land — forget it,
    // or a later discard would show the green "All changes saved".
    else if (dirty) wasSaving.current = false;
  }, [saving, dirty]);

  const triggerSave = useCallback(() => {
    if (onSave && !saving) onSave();
  }, [onSave, saving]);

  // ⌘S / Ctrl+S while the pill is up.
  useEffect(() => {
    if (!mounted || saved) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        triggerSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, saved, triggerSave]);

  if (!mounted) return null;

  const message = saved
    ? "All changes saved"
    : count == null
      ? "You have unsaved changes"
      : `${count} unsaved change${count === 1 ? "" : "s"}`;

  const bar = (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: placement === "container" ? "absolute" : "fixed",
        left: 0,
        right: 0,
        bottom: 20,
        display: "flex",
        justifyContent: "center",
        // The strip spans the viewport so the pill can centre, but only the
        // pill itself takes clicks — the page stays usable around it.
        pointerEvents: "none",
        // Above page chrome and drawers (1200), below modals (1300) — a modal
        // opened over a dirty form should cover the bar, not sit under it.
        zIndex: 1200,
        "@keyframes pillPing": {
          "0%": { transform: "scale(.5)", opacity: 0.6 },
          "70%": { transform: "scale(1.15)", opacity: 0 },
          "100%": { opacity: 0 },
        },
        "@keyframes pillNudge": {
          "0%, 100%": { transform: "translateX(0)" },
          "25%": { transform: "translateX(-6px)" },
          "50%": { transform: "translateX(6px)" },
          "75%": { transform: "translateX(-3px)" },
        },
      }}
    >
      <Box
        sx={{
          pointerEvents: "auto",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          background: PILL.bg,
          color: PILL.text,
          borderRadius: "16px",
          // right/top/bottom = 8px so the 8px buttons nest cleanly; the left
          // side needs more because it starts with text, not a control.
          padding: "8px 8px 8px 18px",
          boxShadow:
            "0 6px 16px rgba(16,32,48,.18), 0 2px 6px rgba(16,32,48,.12)",
          fontSize: "13.5px",
          fontWeight: 500,
          lineHeight: 1.4,
          transform: shown ? "translateY(0)" : "translateY(calc(100% + 28px))",
          opacity: shown ? 1 : 0,
          transition:
            "transform .32s cubic-bezier(.2,.9,.3,1.15), opacity .22s ease",
          ...(nudging ? { animation: "pillNudge .4s" } : null),
          "@media (prefers-reduced-motion: reduce)": {
            transition: "opacity .2s",
            transform: "none",
            animation: "none",
          },
        }}
        onAnimationEnd={() => setNudging(false)}
      >
        {/* status dot — amber + radar ping while dirty, green once saved */}
        <Box
          aria-hidden
          sx={{
            position: "relative",
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            background: saved ? PILL.success : PILL.amber,
            "&::after": saved
              ? undefined
              : {
                  content: '""',
                  position: "absolute",
                  inset: "-4px",
                  borderRadius: "50%",
                  border: `2px solid ${PILL.amber}`,
                  opacity: 0,
                  animation: "pillPing 2s ease-out infinite",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                },
          }}
        />

        {/* Column, not a bare span: `subText` (where the edits actually land,
            what the save will touch) sits under the count on its own line. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          <Box component="span">
            {message}
            {!saved && (
              <Box
                component="span"
                sx={{
                  fontSize: "11px",
                  fontWeight: 400,
                  opacity: 0.55,
                  ml: "6px",
                }}
              >
                {navigator?.platform?.toUpperCase?.().includes("MAC")
                  ? "⌘S to save"
                  : "Ctrl+S to save"}
              </Box>
            )}
          </Box>
          {subText && !saved ? (
            <Box
              component="span"
              sx={{ fontSize: "11px", fontWeight: 400, opacity: 0.55 }}
            >
              {subText}
            </Box>
          ) : null}
        </Box>

        {onDiscard && !saved && (
          <Button
            onClick={onDiscard}
            disabled={saving}
            sx={{
              px: "14px",
              py: "7px",
              minWidth: 0,
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              background: "transparent",
              color: PILL.muted,
              "&:hover": { background: "rgba(255,255,255,.10)", color: "#fff" },
            }}
          >
            Discard
          </Button>
        )}

        <Button
          onClick={triggerSave}
          disabled={saving || saved}
          sx={{
            px: "14px",
            py: "7px",
            minWidth: 0,
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 600,
            background: saved ? PILL.success : teal[600],
            color: "#fff",
            "&:hover": { background: saved ? PILL.success : teal[700] },
            "&.Mui-disabled": {
              background: saved ? PILL.success : teal[600],
              color: "#fff",
              opacity: saved ? 1 : 0.7,
            },
          }}
        >
          {saved ? "Saved ✓" : saving ? "Saving…" : label}
        </Button>
      </Box>
    </Box>
  );

  // `position: fixed` resolves against the VIEWPORT only while no ancestor has
  // a transform/filter/perspective — any of those become the containing block
  // for fixed descendants, and the pill then pins to the bottom of that box
  // instead of the window. That is exactly what happens inside an AG Grid
  // master-detail row (`.ag-row` is positioned with `transform: translateY(…)`),
  // where the bar floated mid-page at the end of the expanded row. Rendering
  // the viewport variant through a portal to <body> takes it out of the
  // transformed subtree so `fixed` means fixed again. `placement="container"`
  // is deliberately NOT portalled — it is absolute by design, positioned
  // against the panel it belongs to.
  if (placement === "container") return bar;
  return createPortal(bar, document.body);
}

PendingChangesBar.propTypes = {
  dirty: PropTypes.bool,
  saving: PropTypes.bool,
  onSave: PropTypes.func,
  onDiscard: PropTypes.func,
  label: PropTypes.string,
  count: PropTypes.number,
  subText: PropTypes.string,
  placement: PropTypes.oneOf(["viewport", "container"]),
};
